import { ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getAuth0ErrorInfo } from '../utils/auth0Errors';
import { PasswordChangeModal } from './PasswordChangeModal';
import { AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const ProtectedRoute = ({
  children,
  fallback = <div className="flex items-center justify-center min-h-screen">Loading...</div>,
}: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    error,
    isFirstLogin,
    isLoadingProfile,
    profileError,
    appUser,
    retryFirstLoginCheck,
  } = useAuth();

  // True from the moment Auth0 says we're logged in until the app profile is
  // actually in hand (or has failed).
  //
  // `isLoadingProfile` on its own is one render too late: it's set inside an
  // effect, and effects run *after* the render that follows Auth0 finishing.
  // In that single in-between render both flags are false, so `children`
  // mounted — and then unmounted on the very next render once the effect set
  // the flag, and mounted again when the profile arrived. The whole app tree
  // was being built, torn down and rebuilt on every page load, which is why
  // every start-up request (the conversation history among them) fired twice
  // in parallel. Deriving the state from `appUser` closes that window.
  const isProfilePending = isAuthenticated && !appUser && !profileError;

  // Show loading while Auth0 is initializing
  if (isLoading) {
    return <>{fallback}</>;
  }

  // Show loading while the app profile is being fetched
  if (isLoadingProfile || isProfilePending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying user configuration...</p>
        </div>
      </div>
    );
  }

  // Show detailed Auth0 error if present
  if (error) {
    const errorInfo = getAuth0ErrorInfo(error);

    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="bg-white border border-red-200 rounded-xl p-8 max-w-md w-full shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <h2 className="text-lg font-semibold text-red-800">{errorInfo.message}</h2>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-3">{errorInfo.description}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">{errorInfo.action}</p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => loginWithRedirect()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>

            {errorInfo.docsUrl && (
              <a
                href={errorInfo.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                View documentation
              </a>
            )}
          </div>

          {import.meta.env.DEV && (
            <details className="mt-6 text-xs">
              <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
                Technical information
              </summary>
              <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                {JSON.stringify(error, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    loginWithRedirect();
    return <>{fallback}</>;
  }

  // Show error if the app profile failed to load (suspended, unauthorized, network error)
  if (profileError) {
    const isSuspended = profileError.toLowerCase().includes('suspended');

    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="bg-white border border-red-200 rounded-xl p-8 max-w-md w-full shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <h2 className="text-lg font-semibold text-red-800">
              {isSuspended ? 'Account suspended' : 'Access denied'}
            </h2>
          </div>

          <div className="mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800">{profileError}</p>
            </div>
            {!isSuspended && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  If you believe this is a mistake, contact your administrator to grant you access.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {isSuspended && (
              <button
                onClick={() => retryFirstLoginCheck()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try again
              </button>
            )}
            <button
              onClick={() => loginWithRedirect({ prompt: 'login' })}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Log in with a different account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show password change modal if first login
  if (isFirstLogin === true) {
    return (
      <>
        <PasswordChangeModal isOpen={true} />
        <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-40">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              Initial setup required
            </h2>
            <p className="text-gray-600">You need to change your password to continue</p>
          </div>
        </div>
      </>
    );
  }

  return <>{children}</>;
};
